import { Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useToast } from "@/comn/hooks";
import { comnUtils } from "@/comn/utils";
import { useTranslation } from "react-i18next";
import { APIS, SF_SCRN_MGMT, SG_SCRN_LANG_LIST, URLS } from "@/tz/go/egaz/osci/ptl/ptl/scrn/services/OsciScrnService";
import { useNavigate } from "react-router-dom";
import { lowerCase, upperCase } from "lodash";

export const OSCI0104005S = (): JSX.Element => {
    const {} = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation();
    const toast = useToast();
    const navigate = useNavigate();
    const modal = useModal();
    const langCodes = ["KO", "EN", "TZ"];

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0088" },
            { area: "comnCd", comnCd: "OSC0095" },
        ],
    });

    const form = {
        scrnMgmt: useForm({ defaultSchema: SF_SCRN_MGMT }),
    };

    const grid = {
        scrnLangList: useGrid({ defaultSchema: SG_SCRN_LANG_LIST }),
    };

    const fetch = {
        getScrnDtl: useFetch({
            api: (data) => APIS.getScrnDtl(data),
            onSuccess: (data: any) => {
                const { scrnDtl } = data;

                if (!comnUtils.isEmpty(scrnDtl.content) && !comnUtils.isEmpty(scrnDtl.content.scrnId)) {
                    form.scrnMgmt.setError("scrnId", { message: "msg.osc.00206" }); // Screen ID already registered. !== 이미 등록된 화면ID 입니다. ==!
                    return;
                }

                modal.openModal({
                    content: "msg.osc.00058", // Confirm save? !== 저장하시겠습니까? ==!
                    onConfirm: () => {
                        form.scrnMgmt.handleSubmit(
                            () => fetch.saveScrnDtl.fetch(),
                            () => toast.showToast({ content: "msg.osc.00195" }), // 필수 입력 항목을 입력하시기 바랍니다.
                        )();
                    },
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // 조회가 실패하였습니다.
        }),
        saveScrnDtl: useFetch({
            api: () => APIS.saveScrnDtl({ ...form.scrnMgmt.getValues(), scrnLangList: grid.scrnLangList.getData() }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00073",
                    close: false,
                    onConfirm: () => {
                        handler.onListBtnClick();
                    },
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00045" }), // 저장이 실패하였습니다.
        }),
    };

    const handler = {
        onListBtnClick: () => navigate(`${URLS.osci0104004q}`),
        onSubmitBtnClick: () => {
            if (!handler.checkScrnId()) return;

            form.scrnMgmt.handleSubmit(
                () => {
                    grid.scrnLangList.clearData();

                    langCodes.forEach((langCd) => {
                        const scrnId = form.scrnMgmt.getValue("scrnId");
                        const scrnNm = form.scrnMgmt.getValue(`scrnNm${langCd}`);
                        const scrnDesc = form.scrnMgmt.getValue(`scrnDesc${langCd}`);
                        if (!comnUtils.isEmpty(scrnNm) || !comnUtils.isEmpty(scrnDesc)) {
                            grid.scrnLangList.addRow({
                                langCd: langCd,
                                scrnNm: scrnNm,
                                scrnDesc: scrnDesc,
                                scrnId: scrnId,
                            });
                        }
                    });

                    fetch.getScrnDtl.fetch(form.scrnMgmt.getValue("scrnId"));
                },
                () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid !== 데이터가 유효하지 않습니다. ==!
            )();
        },
        onFormChagneValue: (key: string, value: any) => {
            if (key === "bsopClsfCd") {
                form.scrnMgmt.setValue("scrnId", lowerCase(value));
                if (!comnUtils.isEmpty(value)) {
                    form.scrnMgmt.setValue("scrnDefnDocId", `UI-${upperCase(form.scrnMgmt.getValue("scrnId"))}`);
                } else {
                    form.scrnMgmt.setValue("scrnDefnDocId", "");
                }
            } else if (key === "ptlTpCd") {
                form.scrnMgmt.setValue("scrnId", lowerCase(form.scrnMgmt.getValue("bsopClsfCd") + value));
                if (!comnUtils.isEmpty(value)) {
                    form.scrnMgmt.setValue("scrnDefnDocId", `UI-${upperCase(form.scrnMgmt.getValue("scrnId"))}`);
                } else {
                    form.scrnMgmt.setValue("scrnDefnDocId", "");
                }
            } else if (key === "scrnId") {
                const bsopClsfCd = form.scrnMgmt.getValue("bsopClsfCd");
                const ptlTpCd = form.scrnMgmt.getValue("ptlTpCd");

                form.scrnMgmt.clearErrors("bsopClsfCd");
                form.scrnMgmt.clearErrors("ptlTpCd");

                if (comnUtils.isEmpty(bsopClsfCd)) {
                    form.scrnMgmt.validate("bsopClsfCd");
                    form.scrnMgmt.setValue("scrnId", "");
                    form.scrnMgmt.setValue("scrnDefnDocId", "");
                    return;
                }

                if (comnUtils.isEmpty(ptlTpCd)) {
                    form.scrnMgmt.validate("ptlTpCd");
                    form.scrnMgmt.setValue("scrnId", "");
                    form.scrnMgmt.setValue("scrnDefnDocId", "");
                    return;
                }

                if (!comnUtils.isEmpty(value)) {
                    const input = comnUtils.replaceEmpty(value, "").toUpperCase();
                    const docId = `UI-${input.substring(0, 4)}-${input.substring(4, 8)}-${input.substring(8)}`;
                    form.scrnMgmt.setValue("scrnDefnDocId", docId);
                } else {
                    form.scrnMgmt.setValue("scrnDefnDocId", "");
                }
            }
        },
        checkScrnId: () => {
            form.scrnMgmt.clearErrors("scrnId");

            const bsopClsfCd = form.scrnMgmt.getValue("bsopClsfCd");
            const ptlTpCd = form.scrnMgmt.getValue("ptlTpCd");
            if (comnUtils.isEmpty(bsopClsfCd)) {
                form.scrnMgmt.validate("bsopClsfCd");
                return false;
            } else if (comnUtils.isEmpty(ptlTpCd)) {
                form.scrnMgmt.validate("ptlTpCd");
                return false;
            }

            const prefix = lowerCase(`${bsopClsfCd}I`);
            const scrnId = form.scrnMgmt.getValue("scrnId");

            if (comnUtils.isEmpty(scrnId)) {
                form.scrnMgmt.validate("scrnId");
                return false;
            } else if (scrnId.length !== 12) {
                form.scrnMgmt.setError("scrnId", { message: "msg.osc.00205" });
                return false;
            } else if (!scrnId.startsWith(prefix)) {
                form.scrnMgmt.setError("scrnId", { message: t("msg.osc.00204", { 0: prefix }) });
                return false;
            }

            return true;
        },
    };

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        {/* Service Category, Portal Type 은 고정값이므로 수정할 수 없도록 한다. */}
                        <Group.Row>
                            <Group.Control
                                {...form.scrnMgmt.schema.bsopClsfCd}
                                edit={false}
                                onChange={(value: any) => {
                                    handler.onFormChagneValue("bsopClsfCd", value);
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.scrnMgmt.schema.ptlTpCd}
                                edit={false}
                                onChange={(value: any) => {
                                    handler.onFormChagneValue("ptlTpCd", value);
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.scrnMgmt.schema.scrnId}
                                onChange={(value: any) => {
                                    handler.onFormChagneValue("scrnId", value);
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.scrnMgmt.schema.scrnDefnDocId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.scrnMgmt.schema.scrnUrlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.scrnMgmt.schema.scrnNmTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.scrnMgmt.schema.scrnNmEN} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.scrnMgmt.schema.scrnNmKO} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section hidden={true}>
                        {" "}
                        <Grid {...grid.scrnLangList.grid} />{" "}
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onSubmitBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
